/* eslint-disable react/prop-types */
import { useWidth } from "./MaintenanceTimeline";
import { fmtDMY } from "../../../lib/instructorView";

/**
 * Validades do instrutor nos próximos 12 meses: "hoje" à esquerda, um marco por validade (PU, médico, licença de
 * instrutor, uniformização, qualificações). Losango = já passou o prazo (preso ao "hoje"); círculo = ainda vem.
 * Cores: vermelho (passou ou falta), âmbar (nos últimos 45 dias ou revalidação por fazer), verde (em dia).
 */

const HORIZON = 365;
const PAD = 12;
const ROW_H = 15;
const BASE_Y = 9;
const LABEL_TOP = 21;
const MONTHS = [0, 3, 6, 9, 12];

const COLOR = {
  bad: { bg: "bg-red-500", text: "text-red-300" },
  warn: { bg: "bg-amber-400", text: "text-amber-200" },
  ok: { bg: "bg-emerald-500", text: "text-emerald-300" },
};

function labelOf(e) {
  const d = fmtDMY(e.date).slice(0, 5);
  return e.overdue ? `${e.label} · expirou` : `${e.label} · ${d}`;
}

export default function ValidityTimeline({ entries }) {
  const [ref, measured] = useWidth();
  const width = measured || 560;
  if (!entries?.length) {
    return <div className="text-[11px] text-slate-500 italic" data-testid="di-validity-timeline-empty">Sem validades nos próximos 12 meses.</div>;
  }
  const usable = Math.max(160, width - 2 * PAD);
  const x = (days) => PAD + (days / HORIZON) * usable;

  const rows = [];
  const placed = entries.map((e) => {
    const px = x(e.days);
    const text = labelOf(e);
    const w = text.length * 6.1 + 6;
    let left = px + 10;
    if (left + w > width - PAD) left = Math.max(PAD, px - 10 - w);
    return { e, px, text, left, w };
  });
  placed.forEach((p) => {
    let r = 0;
    while ((rows[r] || []).some(([a, b]) => p.left < b + 6 && p.left + p.w > a - 6)) r += 1;
    (rows[r] = rows[r] || []).push([p.left, p.left + p.w]);
    p.row = r;
  });
  const height = LABEL_TOP + Math.max(1, rows.length) * ROW_H + 2;

  return (
    <div ref={ref} className="w-full" data-testid="di-validity-timeline">
      <div className="relative h-4 text-[10px] text-slate-500 select-none" aria-hidden="true">
        {MONTHS.map((m) => (
          <span key={m} className={`absolute -translate-x-1/2 whitespace-nowrap ${m === 0 ? "text-slate-300 font-semibold" : ""}`} style={{ left: x((m * HORIZON) / 12) }}>
            {m === 0 ? "Hoje" : `+${m} m`}
          </span>
        ))}
      </div>
      <div className="relative" style={{ height }}>
        {MONTHS.map((m) => (
          <div key={m} className={`absolute top-0 bottom-0 border-l ${m === 0 ? "border-slate-500" : "border-slate-800"}`} style={{ left: x((m * HORIZON) / 12) }} />
        ))}
        {placed.map((p) => (
          <div key={`lead-${p.e.key}`} className="absolute bg-slate-600/70" style={{ left: p.px - 0.5, width: 1, top: BASE_Y, height: LABEL_TOP + p.row * ROW_H + 6 - BASE_Y }} />
        ))}
        {placed.map((p) => (
          <div key={`lab-${p.e.key}`} className={`absolute text-[10.5px] leading-[14px] whitespace-nowrap ${(COLOR[p.e.tone] || COLOR.ok).text}`} style={{ left: p.left, top: LABEL_TOP + p.row * ROW_H }}>
            {p.text}
          </div>
        ))}
        {placed.map((p) => (
          <span
            key={p.e.key}
            title={p.e.detail}
            aria-label={p.e.detail}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: p.px, top: BASE_Y }}
            data-testid={`di-vtl-${p.e.key}`}
          >
            <span className={`block ${p.e.overdue ? "w-[10px] h-[10px] rotate-45 rounded-[2px]" : "w-[11px] h-[11px] rounded-full"} ${(COLOR[p.e.tone] || COLOR.ok).bg} ring-2 ring-slate-900`} />
          </span>
        ))}
      </div>
    </div>
  );
}
